import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react'
import { useOverlayPosition } from 'react-aria'
import { WorkbenchLayoutPortal } from 'src/layouts/WorkbenchLayout'
import { cx } from 'src/utils/cx'
import { formatNum } from 'src/utils/formatNum'
import { useBreakpoint } from 'src/utils/hooks/useBreakpoint'
import type { CompletionItem } from 'src/workers/tagCompleteType'

export interface PromptsCompletionDisplayProps {
  items?: CompletionItem[]
  pos?: {
    left: number
    top: number
  }
  selected?: number
  command?: (props: CompletionItem) => void
  active?: boolean
  time?: number
  onPointerSelectingChange?: (selecting: boolean) => void
}

export interface PromptsCompletionDisplayRef {
  containNode: (node: Node) => boolean
}

function TabIndicator() {
  const isMobile = useBreakpoint('mlg')
  if (isMobile) {
    return null
  }

  return (
    <div className="px-1 py-0.5 rounded-md tracking-wide border border-solid border-gray-400 text-gray-400 scale-75 leading-none">
      TAB
    </div>
  )
}

// copied from https://github.com/troph-team/pixstudio/blob/5ae8207ae8aaa26a25ea5b6e2693cd5f22f55b84/apps/web/src/components/canvas/shape/genbox/tag-complete/PromptsCompletion.tsx#L52-L97
// popularity color code, from least to most popular, from shallow blue gray to deep orange
const POST_COUNT_COLOR_CODES = [
  'bg-gray-400',
  'bg-gray-500',
  'bg-gray-600',
  'bg-gray-700',
  'bg-gray-800',
  'bg-gray-900',
  'bg-yellow-500',
  'bg-yellow-600',
  'bg-yellow-700',
  'bg-yellow-800',
  'bg-yellow-900',
  'bg-orange-500',
  'bg-orange-600',
  'bg-orange-700',
  'bg-orange-800',
  'bg-orange-900',
]
const POST_COUNT_COLOR_CODE_TABLE_MIN_POST_COUNT = 1
const POST_COUNT_COLOR_CODE_TABLE_MAX_POST_COUNT = 1000000
const POST_COUNT_COLOR_CODE_TABLE = (() => {
  const table: [number, number, string][] = []
  // take log10 of post count so that the color code is more evenly distributed. take into account the min and max post count
  // so the last color code should cover [1000000, inf]
  const logMin = Math.log10(POST_COUNT_COLOR_CODE_TABLE_MIN_POST_COUNT)
  const logMax = Math.log10(POST_COUNT_COLOR_CODE_TABLE_MAX_POST_COUNT)
  const logStep = (logMax - logMin) / POST_COUNT_COLOR_CODES.length
  for (let i = 0; i < POST_COUNT_COLOR_CODES.length; i++) {
    table.push([
      Math.pow(10, logMin + logStep * i),
      Math.pow(10, logMin + logStep * (i + 1)),
      POST_COUNT_COLOR_CODES[i],
    ])
  }
  return table
})()

const getColorCode = (postCount: number) => {
  for (const [min, max, color] of POST_COUNT_COLOR_CODE_TABLE) {
    if (postCount >= min && postCount < max) {
      return color
    }
  }
  return POST_COUNT_COLOR_CODES[POST_COUNT_COLOR_CODES.length - 1]
}

export const PromptsCompletionDisplay = forwardRef<
  PromptsCompletionDisplayRef,
  PromptsCompletionDisplayProps
>((props, ref) => {
  const targetRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number>(null)

  const { overlayProps, updatePosition } = useOverlayPosition({
    targetRef,
    overlayRef,
    placement: 'bottom left',
  })
  useLayoutEffect(updatePosition, [updatePosition, props.items])

  const notifyPointerSelecting = (selecting: boolean) => {
    props.onPointerSelectingChange?.(selecting)
  }

  const deferPointerEnd = () => {
    if (typeof window !== 'undefined') {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      rafRef.current = window.requestAnimationFrame(() => {
        notifyPointerSelecting(false)
        rafRef.current = null
      })
    } else {
      notifyPointerSelecting(false)
    }
  }

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId)
    } catch {
      // ignore
    }
    notifyPointerSelecting(true)
  }

  const handlePointerEnd = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    try {
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
    } catch {
      // ignore
    }
    deferPointerEnd()
  }

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [])

  useImperativeHandle(ref, () => {
    return {
      containNode: (node: Node) => overlayRef.current?.contains(node) ?? false,
    }
  }, [])

  if (!props.active) {
    return null
  }
  return (
    <WorkbenchLayoutPortal>
      <div
        ref={targetRef}
        className="z-20 fixed -translate-y-full"
        style={{
          left: props.pos?.left || -1000,
          top: props.pos?.top || -1000,
        }}
      >
        <wbr />
      </div>

      {(props.items?.length ?? 0 > 0) ? (
        <div
          ref={overlayRef}
          {...overlayProps}
          className={cx(
            overlayProps.className,
            'flex flex-col bg-background border border-solid border-gray-100 dark:border-gray-600 min-w-[10rem] max-w-[20vw] rounded-md overflow-hidden py-0.5',
          )}
        >
          {props.items?.map((completion, index) => (
            <button
              key={index}
              className={cx(
                'px-1 py-px w-full text-start flex items-center justify-between gap-1 touch-manipulation',
                index === props.selected
                  ? 'bg-background-light'
                  : 'text-primary-light',
              )}
              type="button"
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerEnd}
              onPointerCancel={handlePointerEnd}
              onClick={() => props.command?.(completion)}
            >
              <span className="flex gap-1 max-w-[20vw] truncate items-center">
                {completion.media && (
                  <img
                    className="min-w-4 size-4 rounded-full object-cover"
                    src={completion.media}
                  />
                )}
                {completion.label}
              </span>
              {completion.post_count ? (
                <div
                  className={cx(
                    'shrink-0 rounded-full px-1 py-0.5 text-[11px] leading-none text-white bg-gray-400',
                    getColorCode(completion.post_count),
                  )}
                >
                  {formatNum(completion.post_count)}
                </div>
              ) : null}
              <div className="flex-1" />
              <div className="shrink-0">
                {index === props.selected && <TabIndicator />}
              </div>
            </button>
          ))}
          {props.time ? (
            <div className="absolute bottom-0 right-0 text-xs text-gray-600 dark:text-gray-400 -translate-x-1/1">
              {props.time.toFixed(1)}ms
            </div>
          ) : null}
        </div>
      ) : null}
    </WorkbenchLayoutPortal>
  )
})

PromptsCompletionDisplay.displayName = 'PromptsCompletionDisplay'
